import { ApiEndpoints } from "@shared/api/api-endpoints";
import { cxApiClient } from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { ServiceOrder } from "../types/consumerRequest";
import { ServiceOrderFilters } from "../types/serviceOrdersTabProps";

// Re-export ServiceOrderFilters for convenience
export type { ServiceOrderFilters } from "../types/serviceOrdersTabProps";

// Service Order Types
export interface CreateServiceOrderPayload {
  sop: number;
  consumerSupportRequestId: number;
  remoteUtilityId: number;
  requestAccountNo: string;
  requestId: number;
  requestUsername: string;
  source: number;
  requestAvailabilityDate: string;
  remark: string;
  mobileNumber?: string;
}

export interface UpdateServiceOrderPayload {
  type?: string;
  subType?: string;
  status?: string;
  scheduledDate?: string;
  assignedTo?: string;
  remarks?: string;
  priority?: "Low" | "Medium" | "High" | "Critical";
  estimatedDuration?: string;
  actualDuration?: string;
  cost?: number;
  materials?: string[];
}

export interface ServiceOrderListResponse {
  result: ServiceOrder[];
}

export interface ServiceOrderDetail extends ServiceOrder {
  remarks?: string;
  priority?: "Low" | "Medium" | "High" | "Critical";
  estimatedDuration?: string;
  actualDuration?: string;
  cost?: number;
  materials?: string[];
  completedOn?: string;
  completedBy?: string;
  approvedOn?: string;
  approvedBy?: string;
  rejectionReason?: string;
}

export interface ServiceOrderTypeChoice {
  id: number;
  name: string;
  shortName: string;
}

export interface ServiceOrderStatusChoice {
  key: string;
  value: string;
}

export interface ServiceOrderPriorityChoice {
  key: string;
  value: string;
}

// Service Order API Functions
export const serviceOrderApi = {
  // Get Service Orders List
  getServiceOrders: async (
    filters: ServiceOrderFilters
  ): Promise<ServiceOrderListResponse> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-request",
      ["service-order"],
      (query) => {
        if (filters.remote_utility_id)
          query.push("remote_utility_id", filters.remote_utility_id.toString());
        if (filters.request_id && filters.request_id !== "NA") query.push("request_id", filters.request_id);
        return query;
      }
    );

    const response = await cxApiClient.get<ServiceOrderListResponse>(url);
    return transformJsonToObject<ServiceOrderListResponse>(response.data);
  },

  // Get Service Order Detail
  getServiceOrderDetail: async (
    serviceOrderId: string
  ): Promise<{ result: ServiceOrderDetail }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "service-orders",
      [serviceOrderId]
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceOrderDetail }>(response.data);
  },

  // Create Service Order
  createServiceOrder: async (
    payload: CreateServiceOrderPayload
  ): Promise<{ result: ServiceOrderDetail }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-request",
      ["service-order"]
    );
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<{ result: ServiceOrderDetail }>(response.data);
  },

  // Update Service Order
  updateServiceOrder: async (
    serviceOrderId: string,
    payload: UpdateServiceOrderPayload
  ): Promise<{ result: ServiceOrderDetail }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "service-orders",
      [serviceOrderId]
    );
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<{ result: ServiceOrderDetail }>(response.data);
  },

  // Delete Service Order
  deleteServiceOrder: async (
    serviceOrderId: string
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "service-orders",
      [serviceOrderId]
    );
    const response = await cxApiClient.delete(url);
    return response.data;
  },

  // Get Service Orders by Consumer
  getServiceOrdersByConsumer: async (params: {
    consumerId: string;
    remoteUtilityId: number;
    requestId?: string;
  }): Promise<{ result: ServiceOrder[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "service-orders/consumer",
      (query) => {
        query.push("consumer_id", params.consumerId);
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.requestId) query.push("request_id", params.requestId);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceOrder[] }>(response.data);
  },

  // Get Service Orders by Request
  getServiceOrdersByRequest: async (params: {
    requestId: string;
    remoteUtilityId: number;
  }): Promise<{ result: ServiceOrder[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "service-orders/request",
      (query) => {
        query.push("request_id", params.requestId);
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceOrder[] }>(response.data);
  },

  // Get Service Order Types
  getServiceOrderTypes: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: ServiceOrderTypeChoice[] }> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "get-all-sops",
      (query) => {
        query.push("remote_utility_id", REMOTE_UTILITY_ID);
        query.push("association_type", "CONSUMER");
        return query;
      }
    );
    const response = await cxApiClient.get<{
      result: ServiceOrderTypeChoice[];
    }>(url);
    return transformJsonToObject<{ result: ServiceOrderTypeChoice[] }>(
      response.data
    );
  },

  // Get Service Order Statuses
  getServiceOrderStatuses: async (): Promise<{
    result: ServiceOrderStatusChoice[];
  }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/service-order-statuses");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceOrderStatusChoice[] }>(
      response.data
    );
  },

  // Get Service Order Priorities
  getServiceOrderPriorities: async (): Promise<{
    result: ServiceOrderPriorityChoice[];
  }> => {
    const url = ApiEndpoints.createUrl(
      "cx",
      "choices/service-order-priorities"
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceOrderPriorityChoice[] }>(
      response.data
    );
  },

  getJobCardPriorities: async () => {
    const url = ApiEndpoints.createUrl("cx", "choices/job_card_priority");
    const response = await cxApiClient.get(url);
    return response.data.result;
  },
};
